// src/components/Dashboard.js
import "./Dashboard.css";

import { useMemo, useState } from "react";
import {
  useWSInCardCreated,
  useWSInCardDeleted,
  useWSInCardDisplayTypeUpdated,
  useWSInTopicCreated,
  useWSInTopicDeleted,
  useWSInTopicUpdated,
} from "../components/hooks/useWebSocket";

import Header from "../components/layout/Header";
import PipelineDrawer from "../components/drawers/PipelineDrawer";
import ResponsiveGrid from "../components/layout/ResponsiveGrid";
import { ToolsSpeedDial } from "../components/layout/ToolsSpeedDial";
import { Typography } from "@material-tailwind/react";
import { deleteCardById } from "../api/api";
import { useDashboardData } from "../components/hooks/useDashboardData";
import { useMessageContext } from "../components/context/MessageContext";
import useModal from "../components/hooks/useModal";
import { useParams } from "react-router-dom";
import { useTopics } from "../components/hooks/useTopicsSimple";
import { useTopicsContext } from "../components/context/TopicsContext";

function Dashboard() {
  const { token } = useParams();
  const { dispatch: messageDispatch } = useMessageContext();
  const { dispatch: topicsDispatch } = useTopicsContext();
  // Use the new hook that returns { cards, topics, layouts, error, loading }
  const {
    cards,
    layouts,
    deleted,
    updateLayouts,
    updateCard,
    createCard,
    cardDeleted,
  } = useDashboardData(token);

  const { topics, loading, error } = useTopics(token);

  const [showPipeline, setShowPipeline] = useState(false);

  const appendMessage = (topicId, message) => {
    messageDispatch({ type: "APPEND_MESSAGE", topicId, message });
  };

  const addTopic = (topic) => {
    topicsDispatch({ type: "ADD_TOPIC", topic });
  };

  const deleteTopic = (topicId) => {
    topicsDispatch({ type: "DELETE_TOPIC", topicId });
  };

  // WebSocket hooks for cards
  useWSInCardDisplayTypeUpdated(updateCard);
  useWSInCardCreated(createCard);
  useWSInCardDeleted(cardDeleted);
  useWSInTopicUpdated(appendMessage);
  useWSInTopicCreated(addTopic);
  useWSInTopicDeleted(deleteTopic);
  // useWSInCardDisplayTypeUpdated(updateCard);

  // Outbound WebSocket hooks remain similar

  // Access Modal Context
  const { openModal } = useModal();

  // Handler to open CardModal for editing a card.
  const handleEditCard = (card) => {
    openModal("card", {
      token,
      card,
      onTrashCanIconPress: () => {
        deleteCardById(token, card.id);
      },
    });
  };

  const pipelineDrawer = useMemo(
    () => (
      <PipelineDrawer
        token={token}
        cards={cards}
        topics={topics}
        pipelineLayouts={layouts?.pipeline || {}}
        updateLayouts={updateLayouts}
        onClose={() => setShowPipeline(false)}
      />
    ),
    [token, cards, layouts, topics]
  );

  return (
    <div>
      <Header />
      {deleted && (
        <div className="bg-red-100 text-red-700 p-2 text-center">
          <p>
            This dashboard has been deleted. Please restore it to access your
            cards.
          </p>
        </div>
      )}

      <div
        className={`p-4 w-full ${
          deleted ? "pointer-events-none opacity-50" : ""
        }`}
      >
        {loading ? (
          <p>Loading dashboard...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : cards && cards.length > 0 ? (
          <ResponsiveGrid
            cards={cards}
            topics={topics}
            layouts={layouts.grid}
            updateLayouts={updateLayouts}
            setSelectedCard={handleEditCard}
          />
        ) : (
          <div className="bg-white rounded-xl text-center mx-auto w-1/2">
            <div className="p-4 shadow-md rounded-xl">
              <Typography className="text-2xl font-bold">
                Welcome to your Dashboard!
              </Typography>
              <Typography className="text-gray-600">
                Here you can create and manage your cards.
              </Typography>
            </div>
          </div>
        )}
      </div>
      <ToolsSpeedDial
        token={token}
        showPipeline={showPipeline}
        setShowPipeline={setShowPipeline}
      />
      {showPipeline && pipelineDrawer}
    </div>
  );
}

export default Dashboard;
